import React, { useEffect, useState } from 'react';
import { Accordion, AccordionBody, AccordionHeader, AccordionItem, Card, ListGroup, ListGroupItem } from 'reactstrap';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

const FaqPage = () => {
  const [open, setOpen] = useState('1');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    ReactGA.send({ hitType: 'FAQView', title: 'FAQ', page: location.pathname });
  }, [location]);

  const toggle = id => {
    setOpen(open === id ? null : id);
  };

  type FaqLink = {
    content: string;
    linkText?: string;
    linkHref?: string;
    suffix?: string;
  };

  type FaqResource = {
    id: string;
    title: string;
    links: FaqLink[];
  };

  const faqs: FaqResource[] = [
    {
      id: '1',
      title: 'What type of data do you collect and why?',
      links: [
        {
          content:
            'We collect basic information such as your name, email address, and phone number when you fill out forms on our website. This helps us process your inquiries, match you with the right resources, and provide tailored services. We may also gather non-personal data (e.g., browser type, IP address) through cookies, Google Analytics, and HubSpot to improve the user experience and analyze website performance.',
        },
      ],
    },
    {
      id: '2',
      title: 'Will my confidential case details be shared with anyone?',
      links: [
        {
          content:
            'Your case information is shared only with the service providers or professionals who need it to evaluate or respond to your request (for example, legal advisors if you’re inquiring about potential legal services). Beyond those specific partners, we do not sell or rent your personal information to third parties. We also do not share your information publicly.',
        },
      ],
    },
    {
      id: '3',
      title: 'How do you keep my personal information secure?',
      links: [
        {
          content:
            'We use industry-standard security measures—including encryption (HTTPS/SSL), secure servers, and restricted access controls—to protect your data from unauthorized access. While no system can be 100% secure, we continuously update our security practices and regularly monitor our systems to safeguard your information.',
        },
      ],
    },
    {
      id: '4',
      title: ' Can employees or staff see my confidential data?',
      links: [
        {
          content:
            'Only authorized personnel who require access for legitimate business purposes (e.g., customer support, service matchmaking) can view your details. We maintain strict internal policies and training to ensure that data access is limited, and all employees must abide by confidentiality agreements.',
        },
      ],
    },
    {
      id: '5',
      title: 'What if I decide not to proceed—can I have my data removed?',
      links: [
        {
          content:
            'You may request to delete or deactivate your information at any time by contacting us. However, please note that certain legal requirements or business obligations may require us to retain specific data for a defined period (e.g., to comply with record-keeping regulations). Once those obligations are satisfied, we will securely delete your data upon request.',
        },
      ],
    },
    {
      id: '6',
      title: ' How is my data used by Google Analytics and HubSpot?',
      links: [
        {
          content:
            'Google Analytics helps us understand how visitors use our website—such as which pages are most popular—so we can improve your experience. It collects anonymized data (e.g., time spent on page, pages visited) and does not include your personal or case details.',
        },
        {
          content:
            'HubSpot is our customer relationship management (CRM) tool. It organizes and tracks your inquiries, contact information, and communication preferences in one secure place, allowing us to provide you with faster, more personalized support.',
        },
      ],
    },
    {
      id: '7',
      title: ' Do you use cookies or other tracking technologies?',
      links: [
        {
          content:
            'Yes. Cookies help us remember your preferences and provide relevant content. We also use them for performance analytics (through Google Analytics) and to tailor communications in HubSpot. You can manage cookie preferences in your browser settings; however, disabling cookies may limit some website features.',
        },
      ],
    },
    {
      id: '8',
      title: 'Will you spam my phone or email after I submit my information?',
      links: [
        {
          content:
            'Absolutely not. We only contact you to provide relevant updates, respond to your inquiries, or share offers you’ve opted into. You can unsubscribe from any email lists or text messages at any time by using the instructions provided in the message or by contacting our support team.',
        },
      ],
    },
    {
      id: '9',
      title: 'What if I have more questions about my privacy or want to report a concern?',
      links: [
        {
          content: 'We encourage you to reach out if you have any additional questions. You can contact us at ',
          linkText: 'doyouhaveacase.ai',
          linkHref: 'https://doyouhaveacase.ai/',
          suffix: '. We take privacy seriously and will address any inquiries or concerns promptly.',
        },
      ],
    },
    {
      id: '10',
      title: 'Is my payment information secure if I decide to purchase anything on your site?',
      links: [
        {
          content:
            'All payment transactions are processed through reputable, PCI-compliant payment gateways. Your financial details (like credit card numbers) are encrypted and transmitted securely. We do not store complete payment information on our servers.',
        },
      ],
    },
    {
      id: '11',
      title: 'Do you have access to my case details?', //Add New Qns
      links: [
        {
          content:
            'No. Your documents are stored using a secure, zero-access architecture, meaning our team cannot view or retrieve any case materials. All evidence is encrypted at rest and in transit, and can only be accessed through your authenticated user session. Additionally, your uploads are not used for model training or internal analytics. This ensures that all sensitive legal information remains fully confidential and controlled solely by you.',
        },
      ],
    },
    {
      id: '12',
      title: 'How is my case information protected on Do You Have a Case?',
      links: [
        {
          content:
            'Do You Have a Case protects user data by hosting on Vultr infrastructure that aligns with HIPAA, SOC, ISO, and PCI frameworks, ensuring encrypted storage, controlled access, and enterprise-grade security throughout the case evaluation process.',
        },
      ],
    },
    {
      id: '13',
      title: 'Does Do You Have a Case comply with HIPAA and data privacy standards?',
      links: [
        {
          content:
            'Do You Have a Case aligns its data handling with HIPAA-ready infrastructure provided by Vultr, supporting privacy safeguards, secure processing, and regulatory-aware controls for sensitive personal and employment-related information.',
        },
      ],
    },
    {
      id: '14',
      title: 'Who can access the information I submit during a case assessment?',
      links: [
        {
          content:
            'User data is accessed only by authorized systems and approved professionals, with infrastructure-level controls enforced through Vultr’s SOC and ISO-aligned security standards to prevent unauthorized access or data exposure.',
        },
      ],
    },
    {
      id: '15',
      title: 'Is my information shared with third parties or used for training AI models?',
      links: [
        {
          content:
            'Do You Have a Case uses submitted information solely for case assessment and matching purposes, operating within Vultr’s compliance framework to restrict misuse, unauthorized sharing, or unapproved secondary data processing.',
        },
      ],
    },
    {
      id: '16',
      title: 'Why does infrastructure compliance matter for a legal case evaluation platform?',
      links: [
        {
          content:
            'Infrastructure compliance ensures that legal and personal data is processed securely, reliably, and lawfully. By aligning with Vultr’s HIPAA, SOC, and ISO controls, Do You Have a Case reduces risk while maintaining user trust.',
        },
      ],
    },
  ];

  return (
    <div className="main-content flex-grow-1 d-flex flex-column gap-5 justify-content-between px-4 pb-4 help-resources-wrapper">
      <div className="custom-container flex-grow-1">
        <div className="help-resources-header mb-3 d-flex flex-wrap gap-3 justify-content-between align-items-center">
          <h1 className="page-title">Frequently Asked Questions</h1>
        </div>
        <Card className="card-wrapper">
          <Accordion open={open} toggle={toggle}>
            {faqs.map(resource => (
              <AccordionItem key={resource.id} className="custom-accordion">
                <AccordionHeader targetId={resource.id}>
                  <div className="step-circle submitted">{resource.id}</div>
                  <span className="p-3 text-left"> {resource.title} </span>
                </AccordionHeader>
                <AccordionBody accordionId={resource.id}>
                  {resource.links.length > 0 ? (
                    <ListGroup flush>
                      {resource.links.map((link, index) => (
                        <div key={index}>
                          <p className="resource-description">
                            {link.content}
                            {link.linkText && (
                              <a
                                href={link.linkHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#007bff',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                }}
                              >
                                {link.linkText}
                              </a>
                            )}
                            {link.suffix}
                          </p>
                        </div>
                      ))}
                    </ListGroup>
                  ) : (
                    <p>No resources available.</p>
                  )}
                </AccordionBody>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      </div>
      <div className="footer-sec">
        <p>
          <b className="privacy-policy" onClick={() => navigate('/privacy-policy')}>
            Privacy Policy
          </b>{' '}
          <b>|</b> <b>Disclaimer:</b> We are not a legal service provider. We connect you to referrals and provide reports and initial
          assessments based on best practices. This does not constitute legal advice. Always consult with legal counsel regarding specifics.
        </p>
      </div>
    </div>
  );
};

export default FaqPage;
